import { Injectable } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signInWithCustomToken, signOut, authState, User, getIdTokenResult, sendPasswordResetEmail, confirmPasswordReset, verifyPasswordResetCode } from '@angular/fire/auth';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DataService } from './services/data';
import { FirestoreService } from './services/firestore';
import { CurrentUserService } from './services/current-user.service';
import { MemberService } from './services/member.service';
import { FamiliesService } from './services/familes.service';
import { Firestore, doc, onSnapshot } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public user$: Observable<User | null>;
  private claimsVersionUnsubscribe: (() => void) | null = null;
  private claimsVersionUid: string | null = null;
  private lastClaimsVersionMillis: number | null = null;
  private refreshingClaims = false;

  constructor(
    private auth: Auth,
    private router: Router,
    private dataService: DataService,
    private firestoreService: FirestoreService,
    private currentUserService: CurrentUserService,
    private memberService: MemberService,
    private familiesService: FamiliesService,
    private firestore: Firestore,
  ) {
    // Escuchar el estado de autenticación de Firebase
    this.user$ = authState(this.auth).pipe(
      switchMap(user => {
        if (user) {
          // Si hay un usuario, obtener sus claims
          return this.handleUserClaims(user);
        } else {
          // Si no hay usuario, limpiar los datos
          this.clearAuthData();
          return of(null);
        }
      })
    );

    // Eliminar esta línea, la redirección a login/root debe ser manejada por AuthGuard
    // this.monitorAuthState();
  }

  private async handleUserClaims(user: User): Promise<User | null> {
    try {
      const tokenResult = await getIdTokenResult(user, true); // Forzar refresh del token
      const claims = tokenResult.claims;
      this.listenForClaimsRefresh(user);

      const churchId = claims['churchId'] as string || null;
      // Multi-Church V2: "churchIds" claim is an array of strings.
      const churchIds = claims['churchIds'] as string[] || [];
      // Legacy "accessibleChurches" might be present or not.
      let accessibleChurches = claims['accessibleChurches'] as any[] || [];

      // If we have churchIds but no accessibleChurches objects, we construct basic ones.
      if (churchIds.length > 0 && accessibleChurches.length === 0) {
        accessibleChurches = churchIds.map(id => ({ id: id, name: 'Cargando...' }));
      }

      const roles = claims['roles'] as string[] || [];
      const access = claims['access'] as string[] || [];

      // Combine roles and access for frontend compatibility
      const allPermissions = [...roles, ...access];
      const supportAccess = claims['supportAccess'] === true;
      const supportChurchId =
        (claims['supportAccessChurchId'] as string) || churchId || null;

      // Actualizar DataService con los datos de los claims
      this.dataService.updateUserRoles(allPermissions);
      this.dataService.updateSupportAccess(supportAccess, supportChurchId);
      this.dataService.updateAccessibleChurches(accessibleChurches);

      // LÓGICA MULTI-IGLESIA
      // Check both the object array length OR the string array length
      if ((accessibleChurches && accessibleChurches.length > 1) || (churchIds && churchIds.length > 1)) {
        // Conservar selección si sigue en churchIds; si no, caer al claim primario o al primero.
        const currentChurchId =
          this.dataService.getChurchId() ||
          localStorage.getItem('selectedChurchId');
        if (currentChurchId && churchIds.includes(currentChurchId)) {
          localStorage.setItem('selectedChurchId', currentChurchId);
          if (this.dataService.getChurchId() !== currentChurchId) {
            this.dataService.updateChurchId(currentChurchId);
          }
        } else {
          const nextChurchId = churchId && churchIds.includes(churchId)
            ? churchId
            : churchIds[0] || accessibleChurches[0]?.id || null;
          if (nextChurchId) {
            localStorage.setItem('selectedChurchId', nextChurchId);
            this.dataService.updateChurchId(nextChurchId);
          }
        }
        return user;
      }

      // FALLBACK / LEGACY / SINGLE CHURCH
      const targetChurchId = churchId || (accessibleChurches.length === 1 ? accessibleChurches[0].id : null);

      if (targetChurchId) {
        this.dataService.updateChurchId(targetChurchId);
      } else {
        await this.logout();
        return null;
      }

      return user;
    } catch (error) {
      await this.logout(); // Perform a full logout in case of error
      return null;
    }
  }

  /**
   * La Cloud Function actualiza AuthSessionVersions/{uid} después de cambiar claims.
   * Así una sesión abierta refresca el token sin esperar a su expiración normal.
   */
  private listenForClaimsRefresh(user: User): void {
    if (this.claimsVersionUid === user.uid && this.claimsVersionUnsubscribe) {
      return;
    }

    this.stopClaimsVersionListener();
    this.claimsVersionUid = user.uid;
    this.lastClaimsVersionMillis = null;

    const versionRef = doc(this.firestore, 'AuthSessionVersions', user.uid);
    this.claimsVersionUnsubscribe = onSnapshot(versionRef, async (snap) => {
      if (!snap.exists()) return;
      const updatedAt = snap.data()?.['updatedAt'];
      const millis =
        updatedAt && typeof updatedAt.toMillis === 'function'
          ? updatedAt.toMillis()
          : 0;

      if (this.lastClaimsVersionMillis === null) {
        this.lastClaimsVersionMillis = millis;
        return;
      }
      if (millis <= this.lastClaimsVersionMillis || this.refreshingClaims) return;

      this.lastClaimsVersionMillis = millis;
      this.refreshingClaims = true;
      try {
        await this.handleUserClaims(user);
      } finally {
        this.refreshingClaims = false;
      }
    });
  }

  private stopClaimsVersionListener(): void {
    this.claimsVersionUnsubscribe?.();
    this.claimsVersionUnsubscribe = null;
    this.claimsVersionUid = null;
    this.lastClaimsVersionMillis = null;
  }

  async login(email: string, password: string): Promise<void> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      if (userCredential.user) {
        // La lógica de handleUserClaims ya se dispara por el observer de authState,
        // así que redirigimos al Splash para que decida.
        this.router.navigate(['/splash']);
      }
    } catch (error) {
      throw error; // Lanzar el error para que el componente de login lo maneje
    }
  }

  async signInWithSupportToken(token: string): Promise<void> {
    await signInWithCustomToken(this.auth, token);
    if (this.auth.currentUser) {
      await getIdTokenResult(this.auth.currentUser, true);
    }
  }

  private clearAuthData(): void {
    this.stopClaimsVersionListener();
    this.dataService.updateChurchId(null);
    this.dataService.updateUserRoles([]);
    this.dataService.updateSupportAccess(false);
    this.dataService.updateAccessibleChurches([]);
  }

  async getCurrentUserAsync(): Promise<User | null> {
    return this.auth.currentUser;
  }

  /**
   * Envía un email de recuperación de contraseña al usuario
   * @param email Email del usuario
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      // Configurar la URL de acción para que redirija a nuestra página personalizada
      const actionCodeSettings = {
        url: window.location.origin + '/auth/reset-password',
        handleCodeInApp: true,
      };

      await sendPasswordResetEmail(this.auth, email, actionCodeSettings);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verifica que el código de recuperación sea válido
   * @param oobCode Código de recuperación de la URL
   * @returns Email del usuario si el código es válido
   */
  async verifyPasswordResetCode(oobCode: string): Promise<string> {
    try {
      const email = await verifyPasswordResetCode(this.auth, oobCode);
      return email;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Confirma el restablecimiento de contraseña con el nuevo password
   * @param oobCode Código de recuperación de la URL
   * @param newPassword Nueva contraseña del usuario
   */
  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, oobCode, newPassword);
    } catch (error) {
      throw error;
    }
  }

  async logout(): Promise<void> {
    this.stopClaimsVersionListener();
    this.currentUserService.unsubscribeCurrentUser();
    this.firestoreService.stopObservingUsers();
    this.memberService.stopListening();
    this.familiesService.stopListening();
    this.dataService.clearAll();
    localStorage.removeItem('selectedChurchId');
    await signOut(this.auth);
    this.router.navigate(['']);
  }
}
